import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  OutlinedInput,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, useState } from 'react';
import { FooterButtons } from '../../components/Dialog/ConfirmDialog/FooterButtons';
import { ScheduleValue } from '../../schemas/SourcingList.schema';
import { isScheduleValid } from './schedule/schedule';
import { ScheduleBlock } from './schedule/ScheduleBlock';
import { ScheduleSummaryBanner } from './schedule/ScheduleSummaryBanner';
import { DIALOG_PX, dialogInputSx, dialogPaperSx } from './sourcing-dialog.styles';

const NAME_PREVIEW_MAX_LENGTH = 60;

interface SaveSearchDialogProps {
  readonly open: boolean;
  readonly defaultName?: string | null;
  readonly query?: string | null;
  readonly saving?: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: (name: string, schedule: ScheduleValue) => void;
}

export const SaveSearchDialog: FC<SaveSearchDialogProps> = ({
  open,
  defaultName,
  query,
  saving = false,
  onCancel,
  onConfirm,
}) => {
  const { colors } = useTheme();

  const getInitialName = () => (defaultName ? defaultName.slice(0, NAME_PREVIEW_MAX_LENGTH).trim() : '');

  const [name, setName] = useState(getInitialName);
  const [schedule, setSchedule] = useState<ScheduleValue>(null);

  const reset = () => {
    setName(getInitialName());
    setSchedule(null);
  };

  const canSave = !saving && name.trim().length > 0 && isScheduleValid(schedule);

  const handleConfirm = () => {
    if (!canSave) {
      return;
    }
    onConfirm(name.trim(), schedule);
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth={false}
      TransitionProps={{ onEnter: reset, onExited: reset }}
      slotProps={{ paper: { sx: dialogPaperSx } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: 18,
          mb: 2,
          px: DIALOG_PX,
        }}
      >
        Save Search
        <IconButton size='small' aria-label='Close' onClick={onCancel} disabled={saving}>
          <CloseIcon sx={{ fontSize: 20, color: colors.neutral[40] }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '10px', px: DIALOG_PX }}>
        <div>
          <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: '4px' }}>
            Search Name <span style={{ color: colors.critical[50] }}>*</span>
          </Typography>
          <OutlinedInput
            fullWidth
            autoFocus
            value={name}
            disabled={saving}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleConfirm();
              }
            }}
            sx={dialogInputSx}
          />
        </div>

        {query && (
          <div>
            <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: '4px' }}>
              Query
            </Typography>
            <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
              {query}
            </Typography>
          </div>
        )}

        <ScheduleBlock value={schedule} onChange={setSchedule} disabled={saving} />

        <ScheduleSummaryBanner schedule={schedule} />
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-start', gap: '4px', py: 12, px: DIALOG_PX }}>
        <FooterButtons
          confirmButtonText='Save'
          onConfirm={handleConfirm}
          onCancel={onCancel}
          onConfirmDisabled={!canSave}
          loading={saving}
        />
      </DialogActions>
    </Dialog>
  );
};
